import { useEffect, useRef, type FormEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Send, ShieldAlert } from 'lucide-react';
import type { GroupPatient } from '../../../domain/GroupPatient';
import { useGroupPatientChat } from '../../../application/useGroupPatientChat';
import { markdownToHtml } from '../../../../../shared/utils/markdown';

interface GroupPatientChatTabProps {
  group: GroupPatient;
  persist?: boolean;
}

/**
 * The Memoria aumentada assistant, asked about a group.
 *
 * Every member has to have consented, as both partners of a couple do. The
 * members are listed by name here — this is their own therapist's screen —
 * but nothing sent to the model carries a name.
 */
export default function GroupPatientChatTab({ group, persist = false }: GroupPatientChatTabProps) {
  const { t } = useTranslation();
  const { messages, isLoading, error, consentRequired, sendMessage } = useGroupPatientChat(group.id, { persist });
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const withoutConsent = group.members.filter((member) => !member.consent_ai_analysis);

  if (withoutConsent.length > 0 || consentRequired) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 px-4 py-16 text-center">
        <ShieldAlert className="h-10 w-10 text-warning-500" aria-hidden="true" />
        <p className="text-base font-medium text-content-secondary">
          {t('patient.chat.coupleConsentRequired')}
        </p>
        {withoutConsent.length > 0 && (
          <ul className="list-inside list-disc text-sm text-content-secondary">
            {withoutConsent.map((member) => (
              <li key={member.id}>{`${member.first_name} ${member.last_name}`}</li>
            ))}
          </ul>
        )}
        <p className="text-sm text-content-secondary">{t('patient.chat.coupleConsentHint')}</p>
      </div>
    );
  }

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    const value = inputRef.current?.value.trim() ?? '';
    if (!value || isLoading) return;
    if (inputRef.current) inputRef.current.value = '';
    await sendMessage(value);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex h-full max-h-[600px] min-h-[400px] flex-col">
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {messages.length === 0 && !isLoading && (
          <p className="mt-8 text-center text-sm text-content-tertiary">{t('patient.chat.placeholder')}</p>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                msg.role === 'user'
                  ? 'whitespace-pre-wrap rounded-br-sm bg-accent text-accent-contrast'
                  : 'rounded-bl-sm bg-surface-sunken text-content-primary'
              }`}
            >
              {msg.role === 'user' ? (
                msg.content
              ) : (
                <div className="chat-markdown" dangerouslySetInnerHTML={{ __html: markdownToHtml(msg.content) }} />
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm bg-surface-sunken px-4 py-2">
              <span className="animate-pulse text-sm text-content-tertiary">{t('patient.chat.loading')}</span>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-status-critical-tint px-4 py-2 text-sm text-status-critical-on-tint">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t('patient.chat.error')}</span>
          <button className="ml-auto underline" onClick={() => inputRef.current?.focus()}>
            {t('patient.chat.retry')}
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex items-end gap-2 border-t border-border-subtle p-3">
        <textarea
          ref={inputRef}
          rows={2}
          maxLength={2000}
          disabled={isLoading}
          onKeyDown={handleKeyDown}
          placeholder={t('patient.chat.placeholder')}
          aria-label={t('patient.chat.placeholder')}
          className="field-base field-default min-h-[44px] flex-1 resize-none rounded-xl px-3 py-2"
        />
        <button
          type="submit"
          disabled={isLoading}
          aria-label={t('patient.chat.send')}
          className="flex h-11 min-h-[44px] w-11 min-w-[44px] items-center justify-center rounded-xl bg-accent text-accent-contrast transition-colors duration-[--duration-fast] hover:bg-accent-hover disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
